import React, { useState, useEffect } from 'react';
import { Save, BookOpen } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BookFormatsManager = () => {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${API}/api/book-formats`)
      .then((r) => r.json())
      .then((d) => setFormats(d.formats || []))
      .catch((e) => console.error('Error fetching formats:', e))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (id, field, value) => {
    setFormats((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${API}/api/admin/book-formats`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formats })
      });
      if (response.ok) {
        setMessage('Book formats updated successfully.');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Failed to update. Please try again.');
      }
    } catch (e) {
      console.error('Error saving formats:', e);
      setMessage('Failed to update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}><p>Loading...</p></div>;
  }

  const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#111111' };
  const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '40px', maxWidth: '820px' }}>
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <BookOpen size={24} color="#111111" />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700', margin: 0, color: '#111111' }}>
          Pre-Order / Book Formats
        </h2>
      </div>
      <p style={{ color: '#666666', fontSize: '0.95rem', marginBottom: '32px' }}>
        Update each format's price and Amazon link. Turn on "Available for pre-order" once you have the link, and the button becomes an active "Pre-order on Amazon" button. Leave it off to show the "coming soon" button.
      </p>

      {formats.map((f) => (
        <div
          key={f.id}
          data-testid={`format-editor-${f.id}`}
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E7E0D4', borderRadius: '10px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        >
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: '600', marginTop: 0, marginBottom: '18px', color: '#111111' }}>
            {f.title}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '18px' }}>
            <div>
              <label style={labelStyle}>Price</label>
              <input type="text" value={f.price} onChange={(e) => updateField(f.id, 'price', e.target.value)} data-testid={`format-price-${f.id}`} style={inputStyle} placeholder="$0.00 AUD" />
            </div>
            <div>
              <label style={labelStyle}>Amazon link (URL)</label>
              <input type="url" value={f.url || ''} onChange={(e) => updateField(f.id, 'url', e.target.value)} data-testid={`format-url-${f.id}`} style={inputStyle} placeholder="https://www.amazon.com.au/dp/..." />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '18px' }}>
            <div>
              <label style={labelStyle}>Button label when not yet available</label>
              <input type="text" value={f.buttonText} onChange={(e) => updateField(f.id, 'buttonText', e.target.value)} data-testid={`format-buttontext-${f.id}`} style={inputStyle} placeholder="Available 21 July" />
            </div>
            <div>
              <label style={labelStyle}>Caption under the button</label>
              <input type="text" value={f.caption} onChange={(e) => updateField(f.id, 'caption', e.target.value)} data-testid={`format-caption-${f.id}`} style={inputStyle} placeholder="Short note under the button" />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '600', color: '#111111' }}>
            <input
              type="checkbox"
              checked={Boolean(f.available)}
              onChange={(e) => updateField(f.id, 'available', e.target.checked)}
              data-testid={`format-available-${f.id}`}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Available for pre-order (shows an active "Pre-order on Amazon" button linking to the URL above)
          </label>
        </div>
      ))}

      {message && (
        <div style={{ marginBottom: '20px', padding: '14px', borderRadius: '8px', backgroundColor: message.includes('success') ? '#E8F5E9' : '#FFEBEE', border: `1px solid ${message.includes('success') ? '#4CAF50' : '#F44336'}`, color: message.includes('success') ? '#2E7D32' : '#C62828' }}>
          {message}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="save-formats-button"
        style={{ padding: '14px 32px', backgroundColor: '#111111', color: '#F4F1E8', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', opacity: saving ? 0.6 : 1 }}
      >
        <Save size={18} />
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

export default BookFormatsManager;
